import { daysSinceWeekStart, type WeekStartDay } from "~/shared/kpi"

// Cube time-dimension granularities (mirror of CUBE_GRANULARITY in connect.ts).
export type YoyGranularity = "day" | "week" | "month"

/** Which compareDateRange window a row came from (weekly keys shift by it). */
export type YoyWindow = "current" | "comparison"

// Stable per-period key used to line a current bucket up with its year-ago
// counterpart when merging a `compareDateRange` result set.
//
// Day and month keys are CALENDAR-based, so the match is a true year-over-year
// even when the two date windows differ in length or start — e.g. a company
// with < 2 years of history, where the comparison window is truncated. (The
// previous daily implementation keyed off each window's own first row of data,
// so an unequal start silently shifted every bucket's comp to less than a year
// prior — a few months, for newer accounts.)
//
//   - month → month of year         (Jan ↔ Jan)
//   - day   → month + day of month  (Mar 15 ↔ Mar 15)
//   - week  → the bucket's company-week-start date, with comparison-window
//     rows keyed as the week starting exactly 364 days later, so each
//     comparison week lands on the current week 52 weeks after it
//
// Weekly deliberately does NOT use week-of-year numbering. Week numbers failed
// two ways: the week-53/week-1 boundary left the final week of every calendar
// year unmatched (that column rendered an empty YoY for a full year), and in
// years following a Saturday-starting year (leap: Friday/Saturday) the
// numbering shifted by one for the entire year, silently comparing every week
// against the week 371 days prior. An exact 364-day pairing has neither
// failure mode and preserves same-weekday alignment.
//
// The comparison merge picks the latest year present for each key, so the most
// recent prior-year period wins; a period with no true year-ago counterpart is
// left unmatched rather than compared against an unrelated recent one.
export function getYoyComparisonKey(
  dateStr: string,
  granularity: YoyGranularity,
  weekStartDay: WeekStartDay,
  window: YoyWindow = "current",
): string {
  const date = new Date(dateStr)

  if (granularity === "month") {
    return `month-${date.getMonth() + 1}`
  }

  if (granularity === "day") {
    return `day-${date.getMonth() + 1}-${date.getDate()}`
  }

  // Weekly: snap to the company's reporting week start day, then key by the
  // resulting calendar date; comparison rows key as the week 364 days later.
  const weekStart = new Date(dateStr)
  weekStart.setDate(
    weekStart.getDate() - daysSinceWeekStart(weekStart.getDay(), weekStartDay),
  )
  if (window === "comparison") {
    weekStart.setDate(weekStart.getDate() + 364)
  }
  const month = String(weekStart.getMonth() + 1).padStart(2, "0")
  const day = String(weekStart.getDate()).padStart(2, "0")
  return `week-${weekStart.getFullYear()}-${month}-${day}`
}

const MS_PER_DAY = 86_400_000

/** Whole days since the epoch for a date-only string (timezone-free). */
function utcDayNumber(dateOnly: string): number {
  return Date.parse(`${dateOnly}T00:00:00Z`) / MS_PER_DAY
}

/**
 * Offset-from-window-start pairing key for EXPLICIT compare windows (the
 * parameterized pipeline path). Calendar-identity keys (getYoyComparisonKey)
 * only match when the comparison window is a whole-year shift — an arbitrary
 * compare window would match ZERO keys and silently blank every YoY cell — so
 * here each bucket keys by its offset from its own window's start instead:
 *
 *   - day   → day index from the window start
 *   - month → calendar-month index from the window start's month
 *   - week  → 7-day bucket index from the week-start-SNAPPED window start;
 *     Cube's first weekly bucket date precedes a non-aligned range start (it
 *     snaps to the company week start), so the anchor snaps the same way —
 *     both anchors land on the week start day, keeping the offset between
 *     them a whole number of weeks
 *
 * This is genuine DATE arithmetic, not positional zipping: a bucket missing
 * from one window (a data gap, or windows of different lengths) simply has no
 * partner at its index — the merge then leaves that bucket's YoY blank (or
 * drops the partnerless comparison bucket) instead of shifting every later
 * pairing by one.
 *
 * All date math runs on the date-only slice parsed as UTC — exact integer day
 * numbers, immune to DST and to the local-vs-UTC parsing split between cube
 * row timestamps and 'YYYY-MM-DD' window strings.
 */
export function getWindowComparisonKey(
  dateStr: string,
  granularity: YoyGranularity,
  weekStartDay: WeekStartDay,
  window: YoyWindow,
  anchors: { currentStart: string; comparisonStart: string },
): string {
  const dateOnly = dateStr.slice(0, 10)
  const anchor =
    window === "current" ? anchors.currentStart : anchors.comparisonStart

  if (granularity === "month") {
    const monthIndex = (s: string) =>
      Number(s.slice(0, 4)) * 12 + Number(s.slice(5, 7))
    return `bucket-${monthIndex(dateOnly) - monthIndex(anchor)}`
  }

  if (granularity === "day") {
    return `bucket-${utcDayNumber(dateOnly) - utcDayNumber(anchor)}`
  }

  const anchorDate = new Date(`${anchor}T00:00:00Z`)
  const snappedAnchor =
    utcDayNumber(anchor) -
    daysSinceWeekStart(anchorDate.getUTCDay(), weekStartDay)
  return `bucket-${Math.floor((utcDayNumber(dateOnly) - snappedAnchor) / 7)}`
}
